import {
  createWaiversInCommonService,
  updateWaiversInCommonService,
  fetchWaiversInCommonService,
  deleteWaiverInCommonService,
  getWaiverDocumentUploadUrl,
  uploadWaiverDocumentToGcs,
  attachWaiverDocumentPaths,
  getWaiverDocumentDownloadUrl,
  mapWaiverLoanId,
  updateApprovedWaiversInCommonService,
  addWaiverChatLine,
  getWaiverChatDocumentUploadUrl,
  getWaiverChatDocumentDownloadUrl,
  markWaiverChatRead,
  patchSaveApplication,
  loanDataBasedOnLoanId,
} from "../../services/network";
import { prefetchUserDisplayNames } from "../../hooks/useUserDisplayName";
import { submissionFlow } from "../../types/enums";
import { EscalationTarget } from "../../utils/enums";

// Shared by the per-test mitigation/compensating factor dropdown (processor
// request + underwriter action) and the loan-level General Waiver request —
// both use the exact same list, so General Waiver's export is just an alias.
export const MITIGATION_FACTOR_OPTIONS = [
  "Credit Score above minimum by 10 points or greater",
  "Repeat borrower with 2 or more prior loans performing/performed",
  "Experienced borrower with at least 4 rental properties",
  "Reduction in property debt service by 10% or greater",
  "DSCR above min by 0.10x or greater",
  "0X30X24 month mortgage history",
  "LTV/LTC below max by 5% or greater",
  "PITIA reserves above minimum",
  "Other",
];

export const GENERAL_WAIVER_MITIGATION_FACTOR_OPTIONS = MITIGATION_FACTOR_OPTIONS;

export const WaiverStatus = {
  PENDING: "Pending Review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  APPROVED_CONDITIONALLY: "Approved Conditionally",
  // Underwriter sent the waiver back to the loan officer for more comments or
  // documents. Not a decision (see isWaiverDecided) — the waiver is still
  // open, just waiting on the LO rather than the underwriter.
  NEEDS_INFO: "Need Additional Info",
} as const;

// The responseType recorded by resetWaiverStatus below — a "reset" is a real
// response entry rather than a locally-cleared one, but it's intentionally
// not in WAIVER_SERVICE_RESPONSE_TYPE/RESPONSE_TYPE_TO_STATUS, so
// getWaiverStatus falls back to WaiverStatus.PENDING for it, same as a
// waiver that's never been touched.
const WAIVER_SERVICE_RE_REQUESTED_RESPONSE_TYPE = "Re-Requested";

// Which statuses represent an underwriter's actual decision (as opposed to
// still awaiting one) — drives whether the compensating-factor/comments
// summary and "decided by" audit line are shown.
export const isWaiverDecided = (status: string): boolean =>
  status === WaiverStatus.APPROVED ||
  status === WaiverStatus.REJECTED ||
  status === WaiverStatus.APPROVED_CONDITIONALLY;

export const MIN_MITIGATION_FACTORS = 2;

export const WAIVER_SERVICE_SOURCE_PLATFORM = "SIZER";

// "EvaluationResult" waivers are keyed off a specific guideline/logic test
// (loanResult.{sizerLoanId}.{testType}.{testKey}); loan-level General Waivers
// (TA-11746) aren't tied to any single test, so they use a placeholder key
// until the waiver-service team defines a real convention for them.
export const GENERAL_WAIVER_TYPE = "GeneralWaiver";
export const EVALUATION_RESULT_WAIVER_TYPE = "EvaluationResult";
const GENERAL_WAIVER_KEY = "TEMP_GENERAL_WAIVER_KEY";

// Maps our internal WaiverStatus values to the waiver service's responseType
// enum strings, sent as part of the response payload posted to
// /waiverservice/waiver/{waiverId}/response.
export const WAIVER_SERVICE_RESPONSE_TYPE: Record<string, string> = {
  [WaiverStatus.APPROVED]: "APPROVED",
  [WaiverStatus.REJECTED]: "REJECTED",
  [WaiverStatus.APPROVED_CONDITIONALLY]: "APPROVED-CONDITIONALLY",
  // Backend contract assumption: the waiver service accepts this responseType
  // — it isn't confirmed against the service's enum yet.
  [WaiverStatus.NEEDS_INFO]: "NEED-ADDITIONAL-INFO",
};

// underwritingStatus recorded on the loan's own escalation chain when an
// underwriter sends a waiver back to the loan officer. Not "Pending", so it
// closes the escalation (see Header.tsx's isEscalatedToCurrentViewer), and
// escalatedTo is empty, so ListView derives "LO Review" for the loan.
export const NEEDS_INFO_ESCALATION_STATUS = "Need Additional Info";

// Tag on the chat line carrying an underwriter's Need Additional Info
// comment — lets the comment be found again even if the waiver service
// doesn't return it on the response.
export const NEEDS_INFO_CHAT_TAG = "NEED-ADDITIONAL-INFO";

const RESPONSE_TYPE_TO_STATUS: Record<string, string> = Object.fromEntries(
  Object.entries(WAIVER_SERVICE_RESPONSE_TYPE).map(([status, responseType]) => [
    responseType,
    status,
  ]),
);

// The record shape used throughout the waiver UI is the waiver service's own
// object shape (as returned by GET /waiverservice/LOAN/{loanId}/waiver and,
// per the same service, by POST create) — there is no separate local
// translation layer. `waiverResponses` is an append-only history: every
// approve/reject/conditional-approve/reset action adds one entry rather than
// overwriting, so switching outcomes (including a reset) never loses the
// prior decision's audit trail.
export interface WaiverMitigate {
  waiverMitigateId?: number;
  description: string;
}

export interface WaiverServiceResponse {
  responseType?: string;
  responseBy?: string;
  responseDate?: string;
  isApproved?: boolean;
  compensatingFactors?: string[];
  comments?: string;
  // Pricing-exception-specific fields (waiverType PricingException) — unused by
  // eligibility-test/General Waiver responses. See src/utils/pricingException.ts.
  discountPointPercentage?: string;
  discountPointAmount?: string;
  emailRequestBody?: {
    orgId?: string;
    evaluationId?: string;
    originalLoanAmount?: string;
    propertyAddress?: string;
    originationPoints?: string;
    totalBuyYield?: string;
    createdByEmail?: string;
  };
}

// One entry in a waiver's chat thread, as returned in waiverChat.chatLines.
// chatLineOrderId is assigned by the service (max existing + 1) — never sent
// by the client on create. partyId is the sender's Keycloak/party id (same
// kind of id as a waiver's own createdBy/responseBy) — resolved to a display
// name via useUserDisplayName, same as elsewhere. readBy is the list of
// party ids that have seen this line, including the sender's own id
// automatically — isWaiverChatUnread below is derived from it rather than
// any separate unread flag, since the service doesn't return one.
// created_on is the sent-on timestamp — snake_case, unlike every other
// timestamp in this API (waiver.createdOn, response.responseDate), so it's
// kept as its own field rather than folded into a camelCase one; both are
// still accepted (see getWaiverChatLineTimestamp) in case that's tightened
// up later.
export interface WaiverChatLine {
  chatLineId?: number;
  chatLineOrderId?: number;
  partyId?: string;
  lineText: string;
  hasDocument?: boolean;
  documentPaths?: string[];
  tags?: string[] | null;
  type?: string;
  readBy?: string[];
  created_on?: string;
  createdOn?: string;
}

// See the WaiverChatLine comment above for why both casings are checked.
export const getWaiverChatLineTimestamp = (
  line: { created_on?: string; createdOn?: string } | undefined,
): string | undefined => {
  return line?.created_on ?? line?.createdOn;
};

// The waiver-service response nests the whole chat thread for a waiver under
// this object rather than as a bare array — chatId/chatSequenceId/status are
// service-managed and not used by the UI today.
export interface WaiverChat {
  chatId?: number;
  chatSequenceId?: number;
  title?: string | null;
  tags?: string[] | null;
  accountType?: string | null;
  accountId?: string | null;
  type?: string | null;
  status?: string;
  chatLines: WaiverChatLine[];
}

export interface WaiverServiceRecord {
  waiverId?: string;
  waiverType: string;
  waiverKeys: string[];
  title: string;
  description: string;
  takeoutPartnerId?: string | null;
  sizerLoanId?: string;
  sourcePlatform?: string;
  taggedPartyIds?: string[];
  // Pricing-exception-specific fields (waiverType PricingException) — unused by
  // eligibility-test/General Waiver records. See src/utils/pricingException.ts.
  interestRate?: string;
  requestedDiscountPointPercentage?: string;
  requestedDiscountPointAmount?: string;
  documentPaths: string[];
  waiverMitigates: WaiverMitigate[];
  waiverInfo: {
    hasDocuments?: boolean;
    hasDiscussions?: boolean;
    isInternal?: boolean;
    finalResponse?: string;
    finalCompensatingFactors?: string[] | null;
  };
  waiverResponses: WaiverServiceResponse[];
  waiverChat?: WaiverChat;
  createdBy?: string;
  createdOn?: string;
}

// Multi-select form state (mitigationFactor/compensatingFactor) is always a
// plain string[] before it's turned into waiverMitigates — kept for that UI
// layer only; the record itself never carries a bare string.
export const asMitigationFactorArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

export const isGeneralWaiverRecord = (waiver: { waiverType?: string }): boolean =>
  waiver?.waiverType === GENERAL_WAIVER_TYPE;

const getWaiverListField = (waiver: { waiverType?: string }) =>
  isGeneralWaiverRecord(waiver) ? "generalWaiverDetails" : "waiverDetails";

// Replaces the matching waiver (by waiverId) in the loan's in-memory waiver
// list and syncs LoansContext, operating purely on the freshest `prev` inside
// the updater — never on a `loanDetails` snapshot passed in by the caller,
// which can go stale within a single synchronous call chain (e.g. creating a
// waiver and then immediately attaching documents to it, before either state
// update has been reflected in a re-render). Falls back to appending when the
// target isn't in the list yet, for that same reason. The waiver service is
// the sole persistence layer for waiver data, so this never calls
// patchSaveApplication.
const replaceWaiverInList = (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  updated: WaiverServiceRecord,
): void => {
  const fieldName = getWaiverListField(updated);
  setLoanDetails?.((prev: any) => {
    const list: any[] = prev?.[fieldName] ?? [];
    const exists = list.some((item) => item?.waiverId === updated.waiverId);
    const updatedList = exists
      ? list.map((item) => (item?.waiverId === updated.waiverId ? updated : item))
      : [...list, updated];
    return { ...prev, [fieldName]: updatedList };
  });
};

// Mirrors ListView.tsx's "Waiver Status" column — kept here as the single
// source of truth for both. "N/A" — no waiver was ever requested. "Open" —
// every requested waiver is still pending. "Approved" — every waiver was
// approved (approved and approved-conditionally count as approved).
// "Partially approved" — at least one waiver was approved but not every one
// (whatever the rest are: pending, rejected, or both) — an approval having
// landed takes priority over any waivers still pending. "In progress" — no
// waiver has been approved yet, but some (not all) are still pending.
// "Rejected" — explicitly verified below: every waiver was rejected (none
// pending, none approved), rather than assumed by elimination.
export const computeWaiverReviewStatus = (
  waiverDetails: WaiverServiceRecord[] | undefined,
  generalWaiverDetails: WaiverServiceRecord[] | undefined,
): string => {
  const allWaivers = [...(waiverDetails ?? []), ...(generalWaiverDetails ?? [])];
  if (!allWaivers.length) return "N/A";
  const statuses = allWaivers.map((waiver) => getWaiverStatus(waiver));
  // Still open either way — a waiver sent back for more info hasn't been
  // decided yet, so it counts as pending for the aggregate.
  const pendingCount = statuses.filter(
    (s) => s === WaiverStatus.PENDING || s === WaiverStatus.NEEDS_INFO,
  ).length;
  if (pendingCount === allWaivers.length) return "Open";

  const approvedCount = statuses.filter(
    (s) => s === WaiverStatus.APPROVED || s === WaiverStatus.APPROVED_CONDITIONALLY,
  ).length;
  if (approvedCount === allWaivers.length) return "Approved";
  if (approvedCount > 0) return "Partially approved";

  // approvedCount is 0 here, so the only way every waiver can be accounted
  // for is if they're all rejected — checked explicitly (rather than assumed
  // by elimination) so an unrecognized/future status value can never be
  // mislabeled "Rejected".
  const rejectedCount = statuses.filter((s) => s === WaiverStatus.REJECTED).length;
  if (rejectedCount === allWaivers.length) return "Rejected";

  if (pendingCount > 0) return "In progress";
  return "Partially approved";
};

// Maps a waiverReviewStatus persisted by an older build onto the current
// labels, so loans not re-synced since still read correctly on the list.
export const normalizeWaiverReviewStatus = (status: unknown): string => {
  switch (status) {
    case undefined:
    case null:
    case "":
    case "-":
      return "N/A";
    case "In Progress":
      return "In progress";
    case "Closed":
      return "Approved / Rejected";
    default:
      return String(status);
  }
};

// Persists the aggregate waiverReviewStatus flag onto the loan's own saved
// document as a minimal partial patch (see DocumentList.tsx's
// `patchSaveApplication(loanId, { documents })` for precedent that
// /online-sizer/update supports updating a single field this way) so
// ListView.tsx can read it directly per row instead of querying every
// loan's waivers individually on every list-page load.
//
// Called from fetchAndSyncWaivers with the waiverDetails/generalWaiverDetails
// it just fetched straight from the waiver service's GET response — the
// authoritative full list, not a locally-merged one — so this is never
// computed from a stale snapshot. persistNewWaiver/persistWaiverAction only
// merge the ONE record they each touch into local state and don't otherwise
// know about a loan's other waivers, which is why WaiverRequestForm.tsx/
// GeneralWaiverRequestForm.tsx/WaiverSummary.tsx all re-fetch via
// fetchAndSyncWaivers right after (matching the pattern WaiverActionForm.tsx
// already used for its own decision actions), rather than this being called
// straight from those.
//
// Fire-and-forget: not a precondition for fetchAndSyncWaivers succeeding
// (same "side effect of saving, not a precondition for it" reasoning
// LoansPagev2.tsx's applySaveApplicationSuccessSideEffects already uses for
// waiver-adjacent side effects) — deliberately not awaited, and the
// patchSaveApplication call is deferred into a microtask so even a
// synchronous throw (e.g. an unmocked import in a test) surfaces as a caught
// rejection instead of breaking the caller.
const syncWaiverReviewStatusFlag = (
  loanId: string | undefined,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiverDetails: WaiverServiceRecord[] | undefined,
  generalWaiverDetails: WaiverServiceRecord[] | undefined,
): void => {
  if (!loanId) return;
  const waiverReviewStatus = computeWaiverReviewStatus(waiverDetails, generalWaiverDetails);
  Promise.resolve()
    .then(() => patchSaveApplication(loanId, { waiverReviewStatus }))
    .then(() => {
      setLoanDetails?.((prev: any) => ({ ...prev, waiverReviewStatus }));
    })
    .catch((err) => {
      console.error("Failed to sync the waiverReviewStatus flag:", err);
    });
};

export const buildTestWaiverKey = (
  loanId: string | undefined,
  testType: string | undefined,
  testKey: string | undefined,
): string => `loanResult.${loanId}.${testType}.${testKey}`;

export const WAIVED_VALUE = "WAIVED";

const parseTestWaiverKey = (
  key: string,
): { loanId: string; testType: string; testKey: string } | null => {
  const match = /^loanResult\.([^.]+)\.([^.]+)\.(.+)$/.exec(key);
  if (!match) return null;
  const [, loanId, testType, testKey] = match;
  return { loanId, testType, testKey };
};

// A test that is also evaluated per property (e.g. bridgeRentalPropertyTest's
// minSquareFootage) lives under propertiesResults.{propertyId}.{section}.
// {testKey} in the rules response, separately from its loanResult entry — so
// its waiver needs a key for that path too, or the property-level result
// never learns it was waived (and the LOS -> TC sync can't approve it).
export const buildPropertyTestWaiverKey = (
  propertyId: string,
  section: string,
  testKey: string,
): string => `propertiesResults.${propertyId}.${section}.${testKey}`;

const PROPERTY_TEST_STATUSES = ["PASS", "FAIL", "WAIVED"];

const resolvePropertiesResults = (
  rulesResponse: any,
  assignedPartner?: string,
): Record<string, any> | undefined => {
  const tpResultMap = rulesResponse?.tpResultMap;
  const partnerResult =
    (assignedPartner && tpResultMap?.[assignedPartner]) ||
    (tpResultMap && Object.values(tpResultMap)[0]) ||
    (assignedPartner && rulesResponse?.[assignedPartner]);
  return rulesResponse?.propertiesResults ?? (partnerResult as any)?.propertiesResults;
};

// Every propertiesResults.{propertyId}.{section}.{testKey} path in the rules
// response where testKey is a pass/fail-style test (a status string, not a
// computed number) — one waiver key per property it appears on.
export const getPropertyTestWaiverKeys = (
  rulesResponse: any,
  testKey: string | undefined,
  assignedPartner?: string,
): string[] => {
  if (!testKey) return [];
  const keys: string[] = [];
  Object.entries(resolvePropertiesResults(rulesResponse, assignedPartner) ?? {}).forEach(
    ([propertyId, property]) => {
      Object.entries((property as any) ?? {}).forEach(([section, tests]) => {
        const status = (tests as any)?.[testKey];
        if (
          typeof status === "string" &&
          PROPERTY_TEST_STATUSES.includes(status.toUpperCase())
        ) {
          keys.push(buildPropertyTestWaiverKey(propertyId, section, testKey));
        }
      });
    },
  );
  return keys;
};

const parsePropertyTestWaiverKey = (
  key: string,
): { propertyId: string; section: string; testKey: string } | null => {
  const match = /^propertiesResults\.([^.]+)\.([^.]+)\.(.+)$/.exec(key);
  if (!match) return null;
  const [, propertyId, section, testKey] = match;
  return { propertyId, section, testKey };
};

// Property-level counterpart of getOverriddenTestResults: derives
// { [propertyId]: { [section]: { [testKey]: "WAIVED" } } } from every
// Approved / Approved Conditionally waiver's propertiesResults.* keys.
export const getOverriddenPropertyTestResults = (
  waiverDetails: { waiverKeys?: string[]; waiverResponses?: WaiverServiceResponse[] }[] | undefined,
): Record<string, Record<string, Record<string, string>>> => {
  const overrides: Record<string, Record<string, Record<string, string>>> = {};

  (waiverDetails ?? []).forEach((waiver) => {
    const status = getWaiverStatus(waiver);
    if (status !== WaiverStatus.APPROVED && status !== WaiverStatus.APPROVED_CONDITIONALLY) {
      return;
    }

    (waiver.waiverKeys ?? []).forEach((key) => {
      const parsed = parsePropertyTestWaiverKey(key);
      if (!parsed) return;
      overrides[parsed.propertyId] = {
        ...overrides[parsed.propertyId],
        [parsed.section]: {
          ...overrides[parsed.propertyId]?.[parsed.section],
          [parsed.testKey]: WAIVED_VALUE,
        },
      };
    });
  });

  return overrides;
};

// Derives { [testType]: { [testKey]: "WAIVED" } } from every per-test waiver
// for this loan currently Approved or Approved Conditionally, for merging
// into a rules-evaluation request's resultObject. Computed fresh from
// waiverDetails every time rather than tracked as separate state, so it can
// never drift out of sync with the actual waiver decisions — a
// rejected/reset waiver (status falls back to PENDING) or a General Waiver
// (placeholder key, doesn't match the loanResult.* pattern) is naturally
// excluded rather than needing to be explicitly removed.
export const getOverriddenTestResults = (
  waiverDetails: { waiverKeys?: string[]; waiverResponses?: WaiverServiceResponse[] }[] | undefined,
  loanId: string | undefined,
): Record<string, Record<string, string>> => {
  const overrides: Record<string, Record<string, string>> = {};
  if (!loanId) return overrides;

  (waiverDetails ?? []).forEach((waiver) => {
    const status = getWaiverStatus(waiver);
    if (status !== WaiverStatus.APPROVED && status !== WaiverStatus.APPROVED_CONDITIONALLY) {
      return;
    }

    (waiver.waiverKeys ?? []).forEach((key) => {
      const parsed = parseTestWaiverKey(key);
      if (parsed?.loanId !== loanId) return;
      overrides[parsed.testType] = {
        ...overrides[parsed.testType],
        [parsed.testKey]: WAIVED_VALUE,
      };
    });
  });

  return overrides;
};

// True when approving the loan should first confirm that no pricing
// adjustment was made: at least one non-critical failed test has an approved
// (or approved-conditionally) waiver, yet the loan was never escalated to
// Capital Markets, so nobody priced for the exception. `criticalTests` is the
// LoansContext shape ({ [partnerId]: string[] of critical test keys }); a
// waived test whose key appears in any partner's list is treated as critical
// and ignored (assumption: the critical list holds the same testKey strings
// the waiver keys carry).
export const needsPricingConfirmationOnApprove = (
  waiverDetails: { waiverKeys?: string[]; waiverResponses?: WaiverServiceResponse[] }[] | undefined,
  loanId: string | undefined,
  criticalTests: Record<string, any> | undefined,
  escalations: { escalatedTo?: string[] }[] | undefined,
): boolean => {
  // Only the current escalation round counts: walk back from the latest entry
  // until one with no targets (the loan went back to the Loan Officer).
  const currentRound: { escalatedTo?: string[] }[] = [];
  for (let i = (escalations ?? []).length - 1; i >= 0; i--) {
    const entry = (escalations ?? [])[i];
    if (!(entry?.escalatedTo ?? []).length) break;
    currentRound.push(entry);
  }
  const wasEscalatedToCapitalMarkets = currentRound.some((entry) =>
    (entry?.escalatedTo ?? []).includes(EscalationTarget.capitalMarket),
  );
  if (wasEscalatedToCapitalMarkets) return false;

  // criticalTests is { [partnerId]: { criticalTestLoanEligibility: string[] } }
  // (older shape: { [partnerId]: string[] }).
  const criticalKeys = new Set<string>();
  Object.values(criticalTests ?? {}).forEach((partner: any) => {
    const list = Array.isArray(partner) ? partner : Object.values(partner ?? {}).flat();
    list.forEach((key) => criticalKeys.add(String(key)));
  });
  const waivedTests = getOverriddenTestResults(waiverDetails, loanId);
  return Object.values(waivedTests).some((tests) =>
    Object.keys(tests).some((testKey) => !criticalKeys.has(testKey)),
  );
};

export interface WaiverTestLink {
  waiver: WaiverServiceRecord;
  testType: string;
  testKey: string;
  status: string;
}

// Every per-test waiver for this loan, resolved against its parsed
// testType/testKey — regardless of status, unlike getOverriddenTestResults
// above (which only cares about Approved/Approved Conditionally, for
// rules-evaluation purposes). Used by refreshWaiversAfterSave to find every
// waiver that might need deleting (test now passes) or reverting (decided,
// still failing, but its underlying data changed). General Waivers (no
// per-test key) and Pricing Exceptions (separate lifecycle, see
// utils/pricingException.ts) are skipped.
export const getEvaluationResultWaiverLinks = (
  waiverDetails: WaiverServiceRecord[] | undefined,
  loanId: string | undefined,
): WaiverTestLink[] => {
  const links: WaiverTestLink[] = [];
  if (!loanId) return links;

  (waiverDetails ?? [])
    .filter((waiver) => waiver?.waiverType === EVALUATION_RESULT_WAIVER_TYPE)
    .forEach((waiver) => {
      (waiver.waiverKeys ?? []).forEach((key) => {
        const parsed = parseTestWaiverKey(key);
        if (parsed?.loanId !== loanId) return;
        links.push({
          waiver,
          testType: parsed.testType,
          testKey: parsed.testKey,
          status: getWaiverStatus(waiver),
        });
      });
    });

  return links;
};

// Finds the waiver already on file for a specific failed test card, matched
// by the same waiverKeys path the create call built it from — the only
// identifier that reliably survives a round trip through the waiver service
// (unlike, say, the display title, which the service doesn't guarantee comes
// back verbatim).
export const findTestWaiver = (
  waiverDetails: { waiverKeys?: string[] }[] | undefined,
  loanId: string | undefined,
  testType: string | undefined,
  testKey: string | undefined,
): WaiverServiceRecord | undefined => {
  if (!testType || !testKey) return undefined;
  const key = buildTestWaiverKey(loanId, testType, testKey);
  return (waiverDetails ?? []).find((waiver) => waiver?.waiverKeys?.includes(key)) as
    | WaiverServiceRecord
    | undefined;
};

export const getLatestWaiverResponse = (
  waiver: { waiverResponses?: WaiverServiceResponse[] } | undefined,
): WaiverServiceResponse | undefined =>
  waiver?.waiverResponses?.[waiver.waiverResponses.length - 1];

export const getWaiverStatus = (
  waiver: { waiverResponses?: WaiverServiceResponse[] } | undefined,
): string => {
  const responseType = getLatestWaiverResponse(waiver)?.responseType;
  return responseType
    ? RESPONSE_TYPE_TO_STATUS[responseType] ?? WaiverStatus.PENDING
    : WaiverStatus.PENDING;
};

export const getWaiverMitigationDescriptions = (
  waiver: { waiverMitigates?: WaiverMitigate[] } | undefined,
): string[] => (waiver?.waiverMitigates ?? []).map((m) => m?.description).filter(Boolean);

// The waiver service only round-trips fields already in its own schema
// (waiverMitigates included) — a bespoke top-level field doesn't survive a
// refresh, so the "Other" free text is folded into the "Other" factor's own
// waiverMitigates entry instead (see buildWaiverCreatePayload) rather than
// sent as its own property. MITIGATION_FACTOR_OPTIONS is a closed,
// frontend-defined set, so any mitigate description outside it can only be
// that free text — this derives it back out for display without needing any
// extra persisted field. Legacy per-test waivers stored it as the waiver
// description instead, which WaiverSummary still renders separately.
export const getWaiverOtherReason = (
  waiver: { waiverMitigates?: WaiverMitigate[] } | undefined,
): string | undefined =>
  getWaiverMitigationDescriptions(waiver).find(
    (description) => !MITIGATION_FACTOR_OPTIONS.includes(description),
  );

// The Compensating Factor summary line should show only the actual selected
// factors — pairs with getWaiverOtherReason, which surfaces the "Other" free
// text on its own instead.
export const getWaiverCompensatingFactorDescriptions = (
  waiver: { waiverMitigates?: WaiverMitigate[] } | undefined,
): string[] =>
  getWaiverMitigationDescriptions(waiver).filter((description) =>
    MITIGATION_FACTOR_OPTIONS.includes(description),
  );

// documentPaths comes back as a bare array of GCS path strings — derive a
// display name from the path's last segment for the UI.
export const getWaiverDocuments = (
  waiver: { documentPaths?: string[] } | undefined,
): { fileName: string; filePath: string }[] =>
  (waiver?.documentPaths ?? []).map((path) => ({
    filePath: path,
    fileName: path?.split("/")?.pop() ?? path,
  }));

// Builds the create-waiver request body for the shared cross-platform waiver
// service (POST /waiverservice/LOAN/{loanId}/waiver). Per-test waivers key off
// the evaluation result path (`loanResult.{sizerLoanId}.{testType}.{testKey}`,
// e.g. `loanResult.123.guidelineTest.maxLeverageArvLtv`); General Waivers use
// a placeholder type/key until that convention is defined. Interest-rate-
// specific fields (interestRate, requestedDiscountPointPercentage/Amount) are
// intentionally omitted — they don't apply to eligibility-test waivers.
// Always created with no documents: the document-upload endpoints require an
// existing waiverId, so any selected files are attached afterward via
// uploadWaiverDocuments once this call has returned one.
export const buildWaiverCreatePayload = (
  loanId: string | undefined,
  assignedPartner: string | undefined,
  waiver: {
    testType?: string;
    testKey?: string;
    testTitle?: string;
    description?: string;
    // Free text captured only when "Other" is selected as a compensating
    // factor. The waiver service only persists fields already in its own
    // schema — a bespoke top-level field doesn't survive a refresh — so this
    // is folded into the "Other" factor's own waiverMitigates entry (a real,
    // round-tripping field) instead, replacing the literal "Other" label.
    // getWaiverOtherReason derives it back out for display. Passed by both
    // per-test and General waivers.
    otherDescription?: string;
    mitigationFactor?: string[];
    isGeneralWaiver?: boolean;
    // Extra propertiesResults.{propertyId}.{section}.{testKey} keys for a
    // test that is also evaluated per property — see getPropertyTestWaiverKeys.
    propertyWaiverKeys?: string[];
  },
) => {
  const isGeneralWaiver = !!waiver.isGeneralWaiver;

  return {
    waiverType: isGeneralWaiver ? GENERAL_WAIVER_TYPE : EVALUATION_RESULT_WAIVER_TYPE,
    waiverKeys: isGeneralWaiver
      ? [GENERAL_WAIVER_KEY]
      : [
          buildTestWaiverKey(loanId, waiver.testType, waiver.testKey),
          ...(waiver.propertyWaiverKeys ?? []),
        ],
    secondaryIdentifierName: "LOAN_STAGE",
    secondaryIdentifierValue: "FES",
    title: isGeneralWaiver ? "General Waiver Request" : (waiver.testTitle ?? ""),
    description: waiver.description ?? "",
    takeoutPartnerId: assignedPartner ?? null,
    sizerLoanId: loanId,
    sourcePlatform: WAIVER_SERVICE_SOURCE_PLATFORM,
    taggedPartyIds: [],
    waiverMitigates: asMitigationFactorArray(waiver.mitigationFactor).map(
      (factor) => ({
        description:
          factor === "Other" && waiver.otherDescription
            ? waiver.otherDescription
            : factor,
      }),
    ),
    documentPaths: [],
    waiverInfo: {
      hasDocuments: false,
      hasDiscussions: false,
      isInternal: false,
      finalResponse: "",
    },
  };
};

// Creates the waiver against the shared waiver service (the sole persistence
// layer — sizer never writes waiver data to its own loan document), then
// merges whatever the service returns over the payload we sent — the create
// response is expected to carry the fields sizer can't generate itself
// (waiverId, createdBy, createdOn) — and appends the result to the
// appropriate local list. Returns the merged record so the caller can chain
// uploadWaiverDocuments off the returned waiverId, or pass it straight to
// onSubmit.
//
// currentUserFallbackName is a defensive fallback, not the primary source:
// if the create response doesn't actually echo back createdBy (observed in
// practice — the waiver card rendered from this record showed no creator
// name at all until the page was refreshed and re-fetched it from the GET
// endpoint, which does include it), useUserDisplayName has nothing to
// resolve and renders blank rather than the raw-id-then-name fallback it
// normally shows.
//
// This is deliberately a human-readable display string, NOT an id to be
// resolved — an earlier version of this fallback used getCookie("partyId"),
// which turned out to be a customer/business-entity id from an unrelated
// /public-api/customer lookup (or a static per-environment config constant),
// never a real Keycloak userId, so it could never resolve via
// useUserDisplayName/getUserDetails no matter how many times it retried.
// This mirrors the already-proven pattern resetWaiverStatus/WaiverActionForm
// use for responseBy: useUserDisplayName echoes back whatever value it's
// given when resolution fails (by design, so the UI never shows nothing),
// so passing a name/email string here means it displays correctly on the
// very first render regardless of whether the /online-sizer/users lookup
// (which "regularly fails to resolve real Keycloak ids" per that same
// pattern's own comment) ever actually succeeds for it.
export const persistNewWaiver = async (
  loanId: string | undefined,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiverRequest: {
    testType?: string;
    testKey?: string;
    testTitle?: string;
    description?: string;
    otherDescription?: string;
    mitigationFactor?: string[];
    isGeneralWaiver?: boolean;
    propertyWaiverKeys?: string[];
  },
  assignedPartner?: string,
  currentUserFallbackName?: string,
): Promise<WaiverServiceRecord> => {
  const payload = buildWaiverCreatePayload(loanId, assignedPartner, waiverRequest);
  const response: any = await createWaiversInCommonService(payload, loanId ?? "");
  const record: WaiverServiceRecord = {
    ...payload,
    waiverResponses: [],
    ...(response?.data ?? {}),
    createdBy: response?.data?.createdBy || currentUserFallbackName,
  };

  // The comment travels to the LOS as one chat line on the new waiver.
  const chatLine = await postWaiverCommentAsChat(record.waiverId, waiverRequest.description);
  const recordWithChat: WaiverServiceRecord = chatLine
    ? {
        ...record,
        waiverChat: {
          ...(record.waiverChat ?? { chatLines: [] }),
          chatLines: [
            ...getWaiverChatLines(record),
            { ...chatLine, lineText: chatLine.lineText ?? (chatLine as any).line ?? waiverRequest.description ?? "" },
          ],
        },
      }
    : record;

  const fieldName = getWaiverListField(recordWithChat);
  setLoanDetails?.((prev: any) => ({
    ...prev,
    [fieldName]: [...(prev?.[fieldName] ?? []), recordWithChat],
  }));

  return recordWithChat;
};

// Uploads each pending file to a presigned per-file GCS URL scoped to this
// waiver (POST .../document/upload-url, then a raw PUT with the Content-Type
// header explicitly cleared — the presigned URL isn't signed with one, so
// sending any value causes a signature mismatch), then records all resulting
// paths on the waiver in a single call (POST .../document). Requires the
// waiver to already exist (waiverId) since the upload-url endpoint is scoped
// to it — call this after persistNewWaiver, not before. No-ops (returning the
// waiver unchanged) when there are no files to upload.
export const uploadWaiverDocuments = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: WaiverServiceRecord,
  files: File[],
): Promise<WaiverServiceRecord> => {
  if (!files.length) return waiver;
  if (!waiver?.waiverId) {
    throw new Error("Cannot upload documents: waiver has no waiverId.");
  }

  const paths: string[] = [];
  for (const file of files) {
    const { data } = await getWaiverDocumentUploadUrl(waiver.waiverId, file.name);
    await uploadWaiverDocumentToGcs(file, data?.uploadUrl);
    paths.push(data?.path);
  }
  await attachWaiverDocumentPaths(waiver.waiverId, paths);

  const updatedRecord: WaiverServiceRecord = {
    ...waiver,
    documentPaths: [...(waiver.documentPaths ?? []), ...paths],
  };
  replaceWaiverInList(setLoanDetails, updatedRecord);
  return updatedRecord;
};

// Resolves a presigned, time-limited download URL for a single already-
// attached document (POST .../document/download-url). realmHeaders must be
// passed through explicitly — see getWaiverDocumentDownloadUrl for why.
export const downloadWaiverDocument = async (
  waiverId: string,
  path: string,
  realmHeaders?: any,
): Promise<string | undefined> => {
  const { data } = await getWaiverDocumentDownloadUrl(waiverId, path, realmHeaders);
  return data?.downloadUrl;
};

// The default line type/tags for a new chat message — this cross-role chat
// currently has no UI for choosing either, so every line is sent the same
// way regardless of whether the sender is the loan processor or underwriter.
const WAIVER_CHAT_LINE_TYPE = "internal";

export const getWaiverChatLines = (
  waiver: { waiverChat?: WaiverChat } | undefined,
): WaiverChatLine[] => waiver?.waiverChat?.chatLines ?? [];

// There's no dedicated unread flag on the waiver-service response — a chat
// is unread for the current user when they aren't yet in the latest line's
// readBy (the sender's own id is included in readBy automatically, so a
// chat you started yourself, with no reply yet, correctly isn't "unread").
export const isWaiverChatUnread = (
  waiver: { waiverChat?: WaiverChat } | undefined,
  currentPartyId: string | undefined,
): boolean => {
  if (!currentPartyId) return false;
  const lines = getWaiverChatLines(waiver);
  const latestLine = lines[lines.length - 1];
  if (!latestLine) return false;
  return !(latestLine.readBy ?? []).includes(currentPartyId);
};

// Uploads any attached files to presigned per-file GCS URLs scoped to this
// waiver's chat (same three-step shape as uploadWaiverDocuments, just a
// chat-scoped upload-url endpoint), then posts the message with the
// resulting paths in a single add-chat-line call — the paths are carried on
// that call's documentPaths rather than a separate "attach" call, unlike the
// waiver-level document flow. Appends the returned chat line to local state
// rather than re-fetching the whole waiver.
export const sendWaiverChatLine = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: WaiverServiceRecord,
  message: string,
  files: File[],
  currentPartyId?: string,
): Promise<WaiverServiceRecord> => {
  if (!waiver?.waiverId) {
    throw new Error("Cannot send chat message: waiver has no waiverId.");
  }

  const documentPaths: string[] = [];
  for (const file of files) {
    const { data } = await getWaiverChatDocumentUploadUrl(waiver.waiverId, file.name);
    await uploadWaiverDocumentToGcs(file, data?.uploadUrl);
    documentPaths.push(data?.path);
  }

  const { data: chatLine } = await addWaiverChatLine(waiver.waiverId, {
    line: message,
    hasDocument: documentPaths.length > 0,
    documentPaths,
    tags: [],
    type: WAIVER_CHAT_LINE_TYPE,
  });

  // The request body's own field is "line" (see addWaiverChatLine), but the
  // service's response — like every other chat line the service returns —
  // is expected to carry the message under "lineText" (WaiverChatLine).
  // Whenever the response doesn't actually include a populated "lineText"
  // (e.g. it echoes the request shape back under "line" instead, or omits
  // the text entirely), falling back to `message` here is what makes the
  // sent line show up immediately instead of only after a refresh re-fetches
  // the record from a read path that does return "lineText" correctly.
  // Falls back to a fully locally-built line only if the service
  // unexpectedly returns no body at all — the sender's own id is included
  // in readBy just like the service does, so this doesn't look "unread" to
  // the sender.
  const newLine: WaiverChatLine = chatLine
    ? {
        ...chatLine,
        lineText: (chatLine as any)?.lineText ?? (chatLine as any)?.line ?? message,
      }
    : {
        lineText: message,
        documentPaths,
        partyId: currentPartyId,
        readBy: currentPartyId ? [currentPartyId] : [],
      };

  const updatedRecord: WaiverServiceRecord = {
    ...waiver,
    waiverChat: {
      ...(waiver.waiverChat ?? { chatLines: [] }),
      chatLines: [...getWaiverChatLines(waiver), newLine],
    },
  };
  replaceWaiverInList(setLoanDetails, updatedRecord);
  return updatedRecord;
};

// Waiver comments are handed to the LOS as a single chat line on the waiver
// (the LOS reads the waiver's chat, not its description), so every comment
// written in the waiver UI is posted through here once, as one line. Fails
// soft: the waiver itself is already created/updated by the time this runs,
// so a chat-post failure is logged rather than reported as a failed request
// (which would invite a duplicate retry).
export const postWaiverCommentAsChat = async (
  waiverId: string | undefined,
  comment: string | undefined,
  tags: string[] = [],
): Promise<ChatLineResult | undefined> => {
  const message = comment?.trim();
  if (!waiverId || !message) return undefined;
  try {
    const { data } = await addWaiverChatLine(waiverId, {
      line: message,
      hasDocument: false,
      documentPaths: [],
      tags,
      type: WAIVER_CHAT_LINE_TYPE,
    });
    return data ?? { lineText: message };
  } catch (err) {
    console.error("Something went wrong while sending the waiver comment to chat. ERROR:", err);
    return undefined;
  }
};

type ChatLineResult = Partial<WaiverChatLine>;

// Resolves a presigned download URL for a single chat attachment. The
// endpoint accepts a batch of paths (§3.3 takes "paths": [...]) but this
// always sends a single-element array and reads back the first result —
// BUFFER: the multi-path response shape isn't confirmed yet, so this guesses
// `downloadUrls` in the same order as the requested paths.
export const downloadWaiverChatDocument = async (
  waiverId: string,
  path: string,
  realmHeaders?: any,
): Promise<string | undefined> => {
  const { data } = await getWaiverChatDocumentDownloadUrl(waiverId, [path], realmHeaders);
  return data?.downloadUrls?.[0] ?? data?.downloadUrl;
};

// Marks this waiver's chat as read for the current user, then reflects the
// read receipt on local state (adding currentPartyId to every chat line's
// readBy) so the "Unread" chip disappears immediately rather than waiting on
// a refetch of the whole waiver. Fails soft, like
// fetchAndSyncWaivers/mapWaiverLoanIdToDestination: viewing a chat shouldn't
// block or error out just because the read-receipt call had trouble.
export const markWaiverChatAsRead = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: WaiverServiceRecord,
  currentPartyId: string | undefined,
): Promise<void> => {
  if (!waiver?.waiverId) return;
  try {
    await markWaiverChatRead(waiver.waiverId);
    if (currentPartyId) {
      const updatedRecord: WaiverServiceRecord = {
        ...waiver,
        waiverChat: {
          ...(waiver.waiverChat ?? { chatLines: [] }),
          chatLines: getWaiverChatLines(waiver).map((chatLine) =>
            (chatLine.readBy ?? []).includes(currentPartyId)
              ? chatLine
              : { ...chatLine, readBy: [...(chatLine.readBy ?? []), currentPartyId] },
          ),
        },
      };
      replaceWaiverInList(setLoanDetails, updatedRecord);
    }
  } catch (err) {
    console.error("Something went wrong while marking the waiver chat as read. ERROR:", err);
  }
};

// Records an underwriter/credit-committee decision (approve/reject/approve-
// conditionally) against the waiver service — the sole persistence layer —
// via POST /waiverservice/waiver/{waiverId}/response, then appends the same
// payload to the record's local waiverResponses history (rather than
// re-fetching) and reflects it in local React state. Throws (rather than
// silently updating local-only state) when the target waiver has no
// waiverId, since there'd be nothing for the recorded decision to be backed
// by remotely.
export const persistWaiverAction = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: {
    waiverId?: string;
    waiverType?: string;
    waiverResponses?: WaiverServiceResponse[];
  },
  responsePayload: WaiverServiceResponse,
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void,
): Promise<void> => {
  if (!waiver?.waiverId) {
    throw new Error("Cannot record this waiver action: missing waiverId.");
  }
  await updateWaiversInCommonService(responsePayload, waiver.waiverId);

  const updatedRecord = {
    ...waiver,
    waiverResponses: [...(waiver.waiverResponses ?? []), responsePayload],
  } as WaiverServiceRecord;
  replaceWaiverInList(setLoanDetails, updatedRecord);
  // Every decision (approve/reject/approve-conditionally, and reset via
  // resetWaiverStatus below) can change getOverriddenTestResults' output —
  // bump the version so the existing debounced evaluation-refresh effects
  // re-fire and the rules engine sees the change.
  setWaiverDecisionVersion?.((prev) => prev + 1);
};

// The underwriter's Need Additional Info comment. Like other decision
// comments, the waiver service may not echo it back on the response itself
// and instead surface it only as a tagged chat line (see
// pricingException.ts's DECISION_CHAT_TAG), so fall back to the latest
// tagged chat line when the response's own comments are empty.
export const getNeedsInfoComment = (waiver: WaiverServiceRecord | undefined): string => {
  const own = getLatestWaiverResponse(waiver)?.comments;
  if (own) return own;
  // The request is also posted as a chat line carrying NEEDS_INFO_CHAT_TAG
  // (see WaiverActionForm), which is what we can rely on when the service
  // doesn't echo the response's comments. Only a needs-info-tagged line —
  // any other tagged line is an unrelated earlier decision comment.
  const tagged = getWaiverChatLines(waiver).filter((line) =>
    line.tags?.some((tag) => /info/i.test(tag)),
  );
  return tagged[tagged.length - 1]?.lineText ?? "";
};

// Every waiver currently sent back to the loan officer, for the loan-level
// banner shown when they open the loan.
export const getNeedsInfoRequests = (
  waivers: WaiverServiceRecord[] | undefined,
): { waiverId?: string; title: string; comment: string; by?: string; on?: string }[] =>
  (waivers ?? [])
    .filter((waiver) => getWaiverStatus(waiver) === WaiverStatus.NEEDS_INFO)
    .map((waiver) => {
      const latest = getLatestWaiverResponse(waiver);
      return {
        waiverId: waiver.waiverId,
        title: waiver.title,
        comment: getNeedsInfoComment(waiver),
        by: latest?.responseBy,
        on: latest?.responseDate,
      };
    });

// Closes the loan's pending escalation (if any) with a terminal entry so the
// loan returns to LO Review after an underwriter asks for more information on
// a waiver. A no-op when nothing is pending — the loan is then already with
// the loan officer. Re-reads the saved document first so it appends to the
// latest escalation chain rather than a stale local copy.
export const returnLoanToLoanOfficer = async (
  loanId: string | undefined,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  decodedTokenData?: any,
  comment?: string,
): Promise<void> => {
  if (!loanId) return;
  const { data } = await loanDataBasedOnLoanId([loanId]);
  const fresh = data?.data?.["0"]?.data;
  const escalations: any[] = Array.isArray(fresh?.escalations) ? fresh.escalations : [];
  if (escalations[escalations.length - 1]?.underwritingStatus !== "Pending") return;

  const updatedEscalations = [
    ...escalations,
    {
      escalatedTo: [],
      underwritingStatus: NEEDS_INFO_ESCALATION_STATUS,
      updatedBy:
        decodedTokenData?.name ||
        decodedTokenData?.email ||
        decodedTokenData?.user_id ||
        "Unknown User",
      updatedOn: new Date().toISOString(),
      rejectionComment: comment ?? "",
    },
  ];
  await patchSaveApplication(loanId, { escalations: updatedEscalations });
  setLoanDetails?.((prev: any) => ({ ...prev, escalations: updatedEscalations }));
};

// Used by WaiverSummary's "Reset Waiver" action. Persists the reset as a real
// response entry (responseType "Re-Requested") via the same waiver-service
// response endpoint the approve/reject/conditional actions use, rather than
// just clearing state locally — so it survives a page refresh like any other
// decision. That responseType isn't in RESPONSE_TYPE_TO_STATUS, so
// getWaiverStatus falls back to WaiverStatus.PENDING for it — a reset waiver
// shows as Pending Review, same as one that's never been touched.
// compensatingFactors carries over the waiver's own originally-requested
// mitigation factors (waiverMitigates), rather than going in empty — a reset
// re-opens the waiver for another decision, it doesn't erase why it was
// requested in the first place.
export const resetWaiverStatus = async (
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  waiver: {
    waiverId?: string;
    waiverType?: string;
    waiverResponses?: WaiverServiceResponse[];
    waiverMitigates?: WaiverMitigate[];
  },
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void,
  decodedTokenData?: any,
  // The loan officer's reply when answering a Need Additional Info request;
  // empty for a plain reset.
  comments = "",
): Promise<void> => {
  await persistWaiverAction(
    setLoanDetails,
    waiver,
    {
      responseType: WAIVER_SERVICE_RE_REQUESTED_RESPONSE_TYPE,
      // See the matching comment in WaiverActionForm.tsx's handleAction —
      // the /online-sizer/users lookup regularly fails to resolve real
      // Keycloak ids, so a human-readable name here displays correctly via
      // the lookup's echo-on-miss fallback regardless of whether it
      // actually succeeds.
      responseBy:
        decodedTokenData?.name ||
        decodedTokenData?.email ||
        decodedTokenData?.user_id ||
        "unknown",
      responseDate: new Date().toISOString(),
      isApproved: false,
      // The loan officer's "Other" free text is a request-side note, not a
      // compensating factor chip — keep it out of the response.
      compensatingFactors: getWaiverCompensatingFactorDescriptions(waiver),
      comments,
    },
    setWaiverDecisionVersion,
  );
};

// Populates loanDetails.waiverDetails/generalWaiverDetails straight from the
// shared waiver service's own response shape — the sole persistence layer
// for waivers, so a freshly loaded loan page has nothing else to read
// existing waivers from, and no translation into a separate local shape is
// needed. Fails soft: a waiver-fetch error is logged and otherwise ignored
// rather than blocking the rest of the loan page from loading.
// Every Keycloak/party id referenced anywhere across a batch of waivers —
// each waiver's own createdBy, every responseBy in its waiverResponses
// history, and every chat line's partyId — deduplicated. Used to warm the
// display-name cache in one shot right after a loan's waivers load, rather
// than each WaiverSummary/WaiverChat instance triggering its own lookup.
export const collectWaiverUserIds = (
  records: { createdBy?: string; waiverResponses?: WaiverServiceResponse[]; waiverChat?: WaiverChat }[] | undefined,
): string[] => {
  const ids = new Set<string>();
  (records ?? []).forEach((record) => {
    if (record.createdBy) ids.add(record.createdBy);
    (record.waiverResponses ?? []).forEach((response) => {
      if (response.responseBy) ids.add(response.responseBy);
    });
    (record.waiverChat?.chatLines ?? []).forEach((chatLine) => {
      if (chatLine.partyId) ids.add(chatLine.partyId);
    });
  });
  return Array.from(ids);
};

// syncReviewStatusFlag defaults to false: fetchAndSyncWaivers itself runs on
// every loan page load and after every unrelated save/escalation (Header.tsx,
// LoansPagev2.tsx, LoanPage.tsx) — none of those should re-patch
// waiverReviewStatus. Only the call sites for an actual waiver action —
// WaiverRequestForm.tsx/GeneralWaiverRequestForm.tsx (create),
// WaiverActionForm.tsx (decision), WaiverSummary.tsx (reset) — pass true.
export const fetchAndSyncWaivers = async (
  loanId: string | undefined,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void,
  syncReviewStatusFlag?: boolean,
): Promise<void> => {
  if (!loanId) return;
  try {
    const { data } = await fetchWaiversInCommonService(loanId, loanId);
    const records: WaiverServiceRecord[] = Array.isArray(data) ? data : data?.data ?? [];
    const waiverDetails = records.filter((record) => !isGeneralWaiverRecord(record));
    const generalWaiverDetails = records.filter((record) => isGeneralWaiverRecord(record));

    // One batched lookup for every user referenced across all of this loan's
    // waivers, instead of each rendered waiver/chat line triggering its own —
    // see useUserDisplayName's cache, which every name lookup in the waiver
    // UI already reads from first.
    prefetchUserDisplayNames(collectWaiverUserIds(records));

    setLoanDetails?.((prev: any) => ({
      ...prev,
      waiverDetails,
      generalWaiverDetails,
    }));
    if (syncReviewStatusFlag) {
      syncWaiverReviewStatusFlag(loanId, setLoanDetails, waiverDetails, generalWaiverDetails);
    }
    // The page's initial rules-evaluation call (fired on mount) can race
    // ahead of this fetch — bumping here guarantees a follow-up evaluation
    // runs with waiverDetails actually populated, so a page refresh always
    // ends up sending the current overrides, not just a waiver decision.
    setWaiverDecisionVersion?.((prev) => prev + 1);
  } catch (err) {
    console.error("Something went wrong while fetching waivers. ERROR:", err);
  }
};

// Audit paths for the two display-only override sections (Pricing card /
// Leverages "Adjusted Max" row) are never real loan/property data — they're
// a manual override layered on top of a computed value, already sent to the
// guideline API through their own dedicated override contract (see
// getPricingAdjustmentAuditFieldPath / getLeverageAdjustmentAuditFieldPath).
// Excluded here so an edit to one of those never gets mistaken for a
// loan-data change that should refresh waivers.
// Runs after every successful save (see handleSaveApplication): refreshes
// every per-test waiver against the latest evaluation result.
//   1. Any waiver (any status) whose test now genuinely passes is dead
//      weight — delete it outright (reuses the same delete endpoint the
//      pricing-exception re-request flow already uses).
//   2. Any DECIDED waiver (Approved/Rejected/Approved Conditionally) whose
//      test still fails, when this save actually changed some loan/property
//      data, might have been decided against numbers that just moved — tell
//      the waiver service about the change and let it decide, server-side,
//      whether to revert that waiver to Requested.
// Fails soft (like fetchAndSyncWaivers/mapWaiverLoanIdToDestination): this is
// a side effect of saving, not a precondition for the save itself.
export const refreshWaiversAfterSave = async (opts: {
  loanId: string | undefined;
  waiverDetails: WaiverServiceRecord[] | undefined;
  rulesResponse: any;
  // Already diffed to only the fields that actually changed, and already
  // keyed the same way the guideline API itself names them — see
  // buildWaiverUpdateRequestData (utils/v2/common.ts), the sole intended
  // source of these two. loanData always carries at least loanId/loanStage.
  loanData: Record<string, any>;
  propertyData: Record<string, any>[];
  loanStage: string;
  loanType: string;
  loanSizerType?: string | null;
  isTestStillFailing: (
    rulesResponse: any,
    loanId: string | undefined,
    testType: string,
    testKey: string,
  ) => boolean;
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined;
  setWaiverDecisionVersion?: (updater: (prev: number) => number) => void;
}): Promise<void> => {
  const {
    loanId,
    waiverDetails,
    rulesResponse,
    loanData,
    propertyData,
    loanStage,
    loanType,
    loanSizerType,
    isTestStillFailing,
    setLoanDetails,
    setWaiverDecisionVersion,
  } = opts;
  if (!loanId) return;

  try {
    const links = getEvaluationResultWaiverLinks(waiverDetails, loanId);

    const nowPassing = links.filter(
      (link) => !isTestStillFailing(rulesResponse, loanId, link.testType, link.testKey),
    );
    await Promise.all(
      nowPassing
        .filter((link) => link.waiver.waiverId)
        .map((link) => deleteWaiverInCommonService(link.waiver.waiverId as string)),
    );

    const nowPassingWaiverIds = new Set(nowPassing.map((link) => link.waiver.waiverId));
    const hasDecidedStillFailing = links.some(
      (link) => isWaiverDecided(link.status) && !nowPassingWaiverIds.has(link.waiver.waiverId),
    );

    // loanData always carries loanId/loanStage — only a genuine edit adds a
    // 3rd key, so this is the "did anything actually change" check.
    const hasEdits = Object.keys(loanData ?? {}).length > 2 || (propertyData?.length ?? 0) > 0;

    let calledUpdateApprovedWaivers = false;
    if (hasDecidedStillFailing && hasEdits) {
      await updateApprovedWaiversInCommonService(loanId, {
        loanStage,
        loanSizerType: loanSizerType ?? undefined,
        loanType,
        updateRequest: { loanData, propertyData },
      });
      calledUpdateApprovedWaivers = true;
    }

    if (nowPassing.length || calledUpdateApprovedWaivers) {
      await fetchAndSyncWaivers(loanId, setLoanDetails, setWaiverDecisionVersion);
    }
  } catch (err) {
    console.error("Something went wrong while refreshing waivers after save. ERROR:", err);
  }
};

// Which platform-id field name the destination loanId gets registered under
// differs by tenant, keyed off the same ViteAppKcRealm value App.tsx already
// uses to tell tenants apart (see its "bplsizer" check). Any realm not listed
// here falls back to the default (Toorak/merchants) tenant's field name.
const WAIVER_DESTINATION_PLATFORM_ID_NAME_BY_REALM: Record<string, string> = {
  bplsizer: "toorakLoanId",
};
const DEFAULT_WAIVER_DESTINATION_PLATFORM_ID_NAME = "originatorPlatformLoanId";

export const getWaiverDestinationPlatformIdName = (kcRealm: string | undefined, loanSubmissionTo: string | undefined): string => {
  if (loanSubmissionTo === submissionFlow.SUBMIT_TO_TC) {
    return WAIVER_DESTINATION_PLATFORM_ID_NAME_BY_REALM["bplsizer"] ??
      DEFAULT_WAIVER_DESTINATION_PLATFORM_ID_NAME;
  }
  return WAIVER_DESTINATION_PLATFORM_ID_NAME_BY_REALM[kcRealm ?? ""] ??
    DEFAULT_WAIVER_DESTINATION_PLATFORM_ID_NAME;
};
  

// Called once a loan has been submitted to its destination platform (POST
// /waiverservice/waiver/loan-id/map) — links the sizer-side loanId used
// throughout the waiver service to whatever loanId the destination platform
// assigned it, so waivers tied to this loan can be traced from either side.
// Fails soft, like fetchAndSyncWaivers: this is a side effect of submission,
// not a precondition for it, so it shouldn't block the user from seeing a
// successful submission if the mapping call itself has trouble.
export const mapWaiverLoanIdToDestination = async (
  sizerLoanId: string | undefined,
  destinationLoanId: string | undefined,
  kcRealm: string | undefined,
  loanSubmissionTo: string | undefined
): Promise<void> => {
  if (!sizerLoanId || !destinationLoanId) return;
  try {
    await mapWaiverLoanId(
      sizerLoanId,
      getWaiverDestinationPlatformIdName(kcRealm, loanSubmissionTo),
      destinationLoanId,
    );
  } catch (err) {
    console.error("Something went wrong while mapping the waiver loan id. ERROR:", err);
  }
};
